import { MetadataInfoDTO } from "../DTO/metadataInfoDTO";
import { MoovStepDTO } from "../DTO/moovStepDTO";
import { MetadataInfo } from "./metadataInfo";
import { MoovStepCustomerParameters } from "./moovStepCustomerParameters";
import { MoovStepDefinition, Parameter } from "./moovStepDefinition";

export class MoovStep{
    public id!:number;
    public sequenceNo!:number;
    public description!:string;
    public moovStepDefinitionId!:number;
    public parameterOptionId?:number;
    public defaultParameters!: DefaultParameter[];
    public customerParameters?:MoovStepCustomerParameters;
    public inputFormatType?:string;
    public inputFormatId?:number;
    public outputFormatType?:string;
    public outputFormatId?:number;
    public canSkipIfNoData?:boolean;
    public canSkipAlways?:boolean;
    public prohibitDefaultParameterOverride?:boolean;

    public metadataInfo?:MetadataInfo[];

    private _moovStepDefinition!:MoovStepDefinition;

    constructor(){
        this.defaultParameters=[];
    }

    get moovStepDefinition(){
        return this._moovStepDefinition;
    }
    set moovStepDefinition(value:MoovStepDefinition){
        this.moovStepDefinitionId = value?.id;
        this._moovStepDefinition=value;
        if(this._moovStepDefinition?.stepType=="md"){
            this.deserializeMetadataInfo();
        }
    }

    get hasCustomerOverrideables(){
        let result:boolean=false;
        if(!this.prohibitDefaultParameterOverride && this.moovStepDefinition?.parameterOptions && this.moovStepDefinition?.parameterOptions.length>0){
            const parameterOption = this.moovStepDefinition.parameterOptions?.find(po=>po.id==this.parameterOptionId);
            parameterOption?.parameters.forEach(p=>{
                if(p.canCustomerOverride && (!p.limitedToConfigLevels || p.limitedToConfigLevels.includes(2))){
                    result=true;
                }
            });
        }
        return result;
    }

    get hasLimitedToCustomerLevelParams(){
        let result:boolean=false;
        const parameterOption = this.moovStepDefinition.parameterOptions?.find(po=>po.id==this.parameterOptionId);
        result = parameterOption?parameterOption.parameters.some(p=> p.limitedToConfigLevels?.length===1 && p.limitedToConfigLevels![0]===2):false;
        return result;
    }

    get hasConfigurationOverrideables(){
        let result:boolean=false;
        if(!this.prohibitDefaultParameterOverride && this.moovStepDefinition?.parameterOptions && this.moovStepDefinition?.parameterOptions.length>0){
            const parameterOption = this.moovStepDefinition.parameterOptions?.find(po=>po.id==this.parameterOptionId);
            result = parameterOption?parameterOption.parameters.some(p=>p.canConfigurationOverride && (!p.limitedToConfigLevels || p.limitedToConfigLevels.includes(3))):false;
        }
        return result;
    }

    get hasLimitedToConfigurationLevelParams(){
        let result:boolean=false;
        const parameterOption = this.moovStepDefinition.parameterOptions?.find(po=>po.id==this.parameterOptionId);
        result = parameterOption?parameterOption.parameters.some(p=> MoovStep.isLimitedToConfigurationLevel(p)):false;
        return result;
    }

    //configuration overrideable params which are not limited to configuration level (have a default/customer fallback); if there are any, the 'use default parameters' toggle is meaningful
    get hasNonLimitedConfigurationOverrideables(){
        let result:boolean=false;
        if(!this.prohibitDefaultParameterOverride && this.moovStepDefinition?.parameterOptions && this.moovStepDefinition?.parameterOptions.length>0){
            const parameterOption = this.moovStepDefinition.parameterOptions?.find(po=>po.id==this.parameterOptionId);
            result = parameterOption?parameterOption.parameters.some(p=>p.canConfigurationOverride && (!p.limitedToConfigLevels || p.limitedToConfigLevels.includes(3)) && !MoovStep.isLimitedToConfigurationLevel(p)):false;
        }
        return result;
    }

    get limitedToConfigurationLevelParameterNames(){
        const parameterOption = this.moovStepDefinition.parameterOptions?.find(po=>po.id==this.parameterOptionId);
        return parameterOption?parameterOption.parameters.filter(p=>MoovStep.isLimitedToConfigurationLevel(p)).map(p=>p.name):[];
    }

    public static isLimitedToConfigurationLevel(p:Parameter){
        return p.limitedToConfigLevels?.length===1 && p.limitedToConfigLevels[0]===3;
    }

    get missingMandatoryParameters(){
        let result:string='';
        if(this.moovStepDefinition?.parameterOptions && this.moovStepDefinition?.parameterOptions?.length>0){
            const parameterOption = this.moovStepDefinition.parameterOptions?.find(po=>po.id==this.parameterOptionId);
            parameterOption?.parameters.forEach(p=>{
                if(p.mandatory && p.defaultValue==undefined ){
                    if(this.customerParameters && this.customerParameters.parameters.length>0){
                        //check for customer parameters first
                        let customerParameter = this.customerParameters.parameters.find(cp=>cp.name==p.name);
                        if(customerParameter  && (customerParameter.value==undefined || customerParameter.value==null || customerParameter.value==='') && (!p.limitedToConfigLevels || p.limitedToConfigLevels.includes(2))){
                            result+=p.name + ', ';
                        }
                    }
                    else{
                        let defaultParameter = this.defaultParameters.find(dp=>dp.name==p.name)
                        if((!defaultParameter || defaultParameter.value==undefined || defaultParameter.value==null || defaultParameter.value==='') && (!p.limitedToConfigLevels || p.limitedToConfigLevels.includes(1)) ){
                            result+=p.name + ', ';
                        }
                    }
                }
            });
            if(result.length>0) result = 'Missing: ' + result.substring(0,result.length-2); //remove trailing comma and blank
        }
        return result;
    }

    public static mapFromDTO(param:MoovStepDTO):MoovStep{ 
        if (!param) throw new Error("parameter is null")
        let obj = new MoovStep();
        try
        {
            obj.id = param.id;
            obj.sequenceNo = param.sequenceNo;
            obj.description = param.description;
            obj.moovStepDefinitionId = param.moovStepDefinitionId;
            obj.parameterOptionId = param.parameterOptionId;
            if(param.defaultParameters) obj.defaultParameters = JSON.parse(param.defaultParameters);
            if(param.customerParameters) obj.customerParameters = MoovStepCustomerParameters.mapFromDTO(param.customerParameters);
            obj.inputFormatType=param.inputFormatType;
            obj.inputFormatId=param.inputFormatId;
            obj.outputFormatType=param.outputFormatType;
            obj.outputFormatId=param.outputFormatId;
            obj.canSkipIfNoData=param.canSkipIfNoData;
            obj.canSkipAlways=param.canSkipAlways;
            obj.prohibitDefaultParameterOverride=param.prohibitDefaultParameterOverride;
        }
        catch(e)
        {
            console.log(e);
            
        }
        return obj;
    }

    public static mapToDTO(param:MoovStep):MoovStepDTO{ 
        if (!param) throw new Error("parameter is null")
        let dto = new MoovStepDTO();
        try
        {
            dto.id = param.id;
            dto.sequenceNo = param.sequenceNo;
            dto.description = param.description;
            dto.moovStepDefinitionId = param.moovStepDefinition.id;
            dto.parameterOptionId = param.parameterOptionId;
            if(param.defaultParameters && param.defaultParameters.length>0) dto.defaultParameters = JSON.stringify(param.defaultParameters);
            if(param.customerParameters) dto.customerParameters = MoovStepCustomerParameters.mapToDTO(param.customerParameters);
            dto.inputFormatType=param.inputFormatType;
            dto.inputFormatId=param.inputFormatId;
            dto.outputFormatType=param.outputFormatType;
            dto.outputFormatId=param.outputFormatId;
            dto.canSkipIfNoData=param.canSkipIfNoData;
            dto.canSkipAlways=param.canSkipAlways;
            dto.prohibitDefaultParameterOverride=param.prohibitDefaultParameterOverride;
        }
        catch(e)
        {
            console.log(e);
            
        }
        return dto;
    }

    private deserializeMetadataInfo(){
        let metadataInfoArray:MetadataInfo[] = [];
        let metadataprotocol=this.defaultParameters.find(dp=>dp.name=="metadataprotocol");
        if(metadataprotocol && metadataprotocol.value){
            let mdiArray=JSON.parse(metadataprotocol.value);
            if(!mdiArray) {
                console.log(this.id);
            }
            mdiArray.forEach((mdi:MetadataInfoDTO)=>{
                metadataInfoArray.push(MetadataInfo.mapFromDTO(mdi));
            });
        }
        this.metadataInfo=metadataInfoArray;
    }
    
}

export class  DefaultParameter{
    public name!:string;
    public value!:string;
}